import {
	getClientRoute,
	getFreelanceProjectRoute,
	getProjectRoute,
} from 'src/utils/breadcrumbUtil'
import type { ReduxUserInfo } from '@/types/apiType'
import { graphqlApi } from './graphqlApi'

export const commonUiApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchCurrentBreadCrumb: builder.query<
			{ route: string; label: string }[],
			{ currentLocation: string; userInfoData: ReduxUserInfo }
		>({
			serializeQueryArgs: (queryArgs) => {
				const { currentLocation } = queryArgs.queryArgs
				if (typeof currentLocation === 'string') {
					return currentLocation.toLowerCase().replace(/\/$/, '')
				}
				if (currentLocation == null) return ''
				// Support objects that may carry a `route` field
				const maybeRoute = (currentLocation as any)?.route
				if (typeof maybeRoute === 'string') {
					return maybeRoute.toLowerCase().replace(/\/$/, '')
				}
				// Fallback to JSON string for non-string args
				try {
					return JSON.stringify(currentLocation)
				} catch {
					return String(currentLocation)
				}
			},
			async onQueryStarted(
				{ currentLocation, userInfoData },
				{ dispatch, queryFulfilled }
			) {
				// Optimistically show a basic breadcrumb using the URL segment
				try {
					const clientMatch = currentLocation.match(
						/^\/clients\/([^/?#]+)/
					)
					const projectMatch = currentLocation.match(
						/^\/projects\/([^/?#]+)/
					)
					const id = (clientMatch?.[1] || projectMatch?.[1]) ?? null
					if (id) {
						const patchResult = dispatch(
							commonUiApi.util.updateQueryData(
								'fetchCurrentBreadCrumb',
								{ currentLocation, userInfoData },
								(draft) => {
									if (
										!Array.isArray(draft) ||
										draft.length === 0
									) {
										// minimal optimistic value until real labels arrive
										draft.length = 0
										draft.push({
											route: currentLocation,
											label: id,
										})
									}
								}
							)
						)
						try {
							const fulfilled = await queryFulfilled
							const resolvedCrumbs = (fulfilled.data ?? []) as {
								route: string
								label: string
							}[]
							// Seed cache for each crumb's location with appropriate slice
							resolvedCrumbs.forEach((_, idx) => {
								const targetArg = resolvedCrumbs[idx].route
								const slice = resolvedCrumbs.slice(0, idx + 1)
								dispatch(
									commonUiApi.util.updateQueryData(
										'fetchCurrentBreadCrumb',
										{
											currentLocation: targetArg,
											userInfoData,
										},
										(draft) => {
											draft.length = 0
											slice.map((c) =>
												draft.push({ ...c })
											)
										}
									)
								)
							})
						} catch {
							patchResult.undo()
						}
					} else {
						await queryFulfilled
					}
				} catch {
					// ignore optimistic errors
				}
			},
			keepUnusedDataFor: 300,
			async queryFn({ currentLocation, userInfoData }, api) {
				try {
					// Return empty for top-level pages
					const emptyBreadcrumbPaths = [
						'/',
						'/clients',
						'/newPassword',
						'/freelancer',
						'/subTeamAdmin',
					].map((p) => p.toLowerCase())
					if (
						emptyBreadcrumbPaths.includes(
							currentLocation.toLowerCase()
						)
					) {
						return { data: [] }
					}

					// Loose typing to avoid circular typing imports

					const tenantId = userInfoData?.currentCompanyTenantId
					const groups = userInfoData?.cognitoGroup

					const clientRegex = /^\/clients.*$/
					const checkClient = clientRegex.test(currentLocation)

					const projectRegex = /^\/projects.*$/
					const checkProject = projectRegex.test(currentLocation)

					let id: string | null = null
					if (checkClient) {
						// Extract client ID from /clients/{id} or /clients/{id}/something
						const clientMatch = currentLocation.match(
							/^\/clients\/([^/?#]+)/
						)
						id = clientMatch?.[1] ?? null
					} else if (checkProject) {
						// Extract project ID from /projects/{id} or /projects/{id}/something
						const projectMatch = currentLocation.match(
							/^\/projects\/([^/?#]+)/
						)
						id = projectMatch?.[1] ?? null
					}

					if (checkClient && id) {
						const route = await getClientRoute({
							currentLocation,
							id,
							tenantId: tenantId ?? '',
						})
						return { data: [route] }
					}

					if (
						checkProject &&
						id &&
						!['freelancer', 'subTeamAdmin'].some((i) =>
							(groups ?? []).includes(i)
						)
					) {
						const routeArr =
							(await getProjectRoute({
								currentLocation,
								id,
								tenantId: tenantId ?? '',
							})) || []
						return { data: routeArr.reverse() }
					}

					if ((groups ?? []).includes('freelancer') && id) {
						const routeArr =
							(await getFreelanceProjectRoute({
								currentLocation,
								id,
								tenantId: tenantId ?? '',
							})) || []
						return { data: routeArr.reverse() }
					}

					if (id) {
						const routeArr =
							(await getFreelanceProjectRoute({
								currentLocation,
								id,
								tenantId: tenantId ?? '',
							})) || []
						return { data: routeArr.reverse() }
					}

					// If no valid ID found, return empty breadcrumbs
					return { data: [] }
				} catch (error) {
					return { error: error as any }
				}
			},
			providesTags: () => [{ type: 'Client' }, { type: 'Projects' }],
		}),
	}),
})

export const { useFetchCurrentBreadCrumbQuery } = commonUiApi
